// AI performance analysis — model picker, focus preset, extra prompt, history.
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { dtShort } from "./amFmt";

export default function AMAIPanel({ am, days, history, onNew }) {
  const [meta, setMeta] = useState(null);
  const [focus, setFocus] = useState("overall");
  const [model, setModel] = useState("");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(0);
  useEffect(() => {
    api.get("/am-reports-meta").then(({ data }) => { setMeta(data); setModel(`${data.default.provider}|${data.default.model}`); }).catch(() => {});
  }, []);
  const run = async () => {
    setBusy(true);
    try {
      const [provider, m] = model.split("|");
      const { data } = await api.post(`/am-reports/${am.id}/ai`, { focus, provider, model: m, extra, days }, { timeout: 180000 });
      onNew(data); setSel(0); toast.success("Analysis ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI analysis failed"); }
    finally { setBusy(false); }
  };
  const cur = history[sel];
  return (
    <div className="space-y-3" data-testid="am-ai-panel">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {(meta?.focus || []).map((f) => (
            <button key={f.key} type="button" onClick={() => setFocus(f.key)} title={f.prompt} data-testid={`am-ai-focus-${f.key}`}
              className={`px-2.5 py-1 rounded-full text-[11.5px] border ${focus === f.key ? "bg-fuchsia-500/15 border-fuchsia-400/50 text-fuchsia-200" : "border-white/10 text-slate-400 hover:text-white"}`}>{f.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="am-ai-model"
            className="px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-slate-200">
            {(meta?.models || []).map((m) => <option key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label}</option>)}
          </select>
          <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Fine-tune: e.g. focus on Gold customers, or compare to last month…" data-testid="am-ai-extra"
            className="flex-1 min-w-[220px] px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-white outline-none focus:border-fuchsia-400/60" />
          <button type="button" onClick={run} disabled={busy || !model} data-testid="am-ai-run"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-100 text-[12px] font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy ? "Analysing…" : "Analyse"}
          </button>
        </div>
        <div className="text-[10.5px] text-slate-500">The base prompt is editable in Lara → AI Config → "AM Performance Analysis".</div>
      </div>
      {history.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {history.map((h, i) => (
            <button key={h.id} type="button" onClick={() => setSel(i)} data-testid={`am-ai-hist-${i}`}
              className={`px-2 py-1 rounded-md text-[10.5px] font-mono border ${sel === i ? "border-fuchsia-400/50 text-fuchsia-200" : "border-white/10 text-slate-400"}`}>{h.focus} · {dtShort(h.created_at)}</button>
          ))}
        </div>
      )}
      {busy && <div className="flex items-center gap-2 text-[12px] text-fuchsia-200" data-testid="am-ai-running"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running a new {focus} analysis — this can take up to a minute…</div>}
      {cur ? (
        <div style={{ opacity: busy ? 0.35 : 1, transition: "opacity .2s" }} className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/[0.04] p-4 prose prose-invert prose-sm max-w-none am-ai-output" data-testid="am-ai-output">
          <div className="text-[10.5px] font-mono text-slate-400 mb-2 not-prose">{cur.model} · by {cur.by} · {dtShort(cur.created_at)}{cur.extra ? ` · "${cur.extra}"` : ""}</div>
          <ReactMarkdown>{cur.text}</ReactMarkdown>
        </div>
      ) : <div className="text-center text-slate-500 text-[12.5px] py-8">No analysis yet — pick a focus and click Analyse.</div>}
    </div>
  );
}
